import { roundToTwoDecimals } from '@fruitful/fruitils';

/**
 * Social Security Benefit Calculator
 *
 * Calculates annual Social Security benefits based on:
 * - Average Indexed Monthly Earnings (AIME)
 * - Primary Insurance Amount (PIA) with bend points
 * - Age-based adjustments for early/delayed retirement
 *
 * This is a simplified implementation that assumes consistent income
 * throughout the working career and uses current SSA formulas.
 */
export class SocialSecurityBenefitCalculator {
    // Social Security constants for 2025
    private static readonly BEND_POINT_1 = 1_226; // First bend point for PIA calculation
    private static readonly BEND_POINT_2 = 7_391; // Second bend point for PIA calculation
    private static readonly PIA_PERCENTAGE_1 = 0.9; // 90% of first bend point
    private static readonly PIA_PERCENTAGE_2 = 0.32; // 32% of second segment
    private static readonly PIA_PERCENTAGE_3 = 0.15; // 15% of third segment
    private static readonly FULL_RETIREMENT_AGE = 67; // For those born 1960 or later
    private static readonly DELAYED_RETIREMENT_CREDIT_PER_YEAR = 0.08; // 8% per year after FRA until 70
    private static readonly MAXIMUM_TAXABLE_EARNINGS_2025 = 176_100; // SSA wage base for 2025
    private static readonly WORKING_YEARS = 35; // Number of years used for AIME calculation
    private static readonly MONTHS_PER_YEAR = 12;

    constructor(
        private readonly currentAge: number,
        private readonly socialSecurityBenefitAge: number,
        private readonly grossAnnualIncome: number,
    ) {}

    public calculate(): number {
        // Calculate AIME (Average Indexed Monthly Earnings)
        const aime = this.calculateAIME();

        // Calculate PIA (Primary Insurance Amount) using bend points
        const pia = this.calculatePIA(aime);

        // Apply age-based adjustments
        const adjustedMonthlyBenefit = this.applyAgeAdjustments(pia);

        // Convert to annual benefit
        const annualBenefit =
            adjustedMonthlyBenefit *
            SocialSecurityBenefitCalculator.MONTHS_PER_YEAR;

        return roundToTwoDecimals(annualBenefit);
    }

    /**
     * Calculate taxable portion of Social Security benefits based on income phaseouts
     * @param socialSecurityBenefits - Total Social Security benefits
     * @param otherIncome - Other income (distributions, etc.)
     * @param taxFilingStatus - Tax filing status for phaseout thresholds
     * @returns Taxable portion of Social Security benefits
     */
    public static calculateTaxableSocialSecurity(
        socialSecurityBenefits: number,
        otherIncome: number,
        taxFilingStatus: string,
    ): number {
        if (socialSecurityBenefits === 0) {
            return 0;
        }

        // Calculate provisional income (other income + 50% of Social Security benefits)
        const provisionalIncome = otherIncome + socialSecurityBenefits * 0.5;

        // Get Social Security phaseout thresholds based on filing status
        const thresholds =
            SocialSecurityBenefitCalculator.getSocialSecurityPhaseoutThresholds(
                taxFilingStatus,
            );

        let taxableAmount = 0;

        if (provisionalIncome <= thresholds.base) {
            // No Social Security benefits are taxable
            taxableAmount = 0;
        } else if (provisionalIncome <= thresholds.phaseout) {
            const excessOverBase = provisionalIncome - thresholds.base;
            // Tax excess at 50%, cannot exceed 50% of Social Security benefits.
            taxableAmount = Math.min(
                excessOverBase * 0.5,
                socialSecurityBenefits * 0.5,
            );
        } else {
            // Tax above the phaseout at 85%, include taxable amount at the 50% rate; cannot exceed 85% of social security benefits.
            taxableAmount = Math.min(
                0.85 * (provisionalIncome - thresholds.phaseout) +
                    0.5 * (thresholds.phaseout - thresholds.base),
                0.85 * socialSecurityBenefits,
            );
        }

        return roundToTwoDecimals(taxableAmount);
    }

    /**
     * Get Social Security phaseout thresholds based on filing status
     * @param taxFilingStatus - Tax filing status
     * @returns Object with base and phaseout thresholds
     */
    private static getSocialSecurityPhaseoutThresholds(
        taxFilingStatus: string,
    ): { base: number; phaseout: number } {
        switch (taxFilingStatus) {
            case 'MARRIED_FILING_JOINTLY':
                return { base: 32000, phaseout: 44000 };
            case 'SINGLE':
            case 'MARRIED_FILING_SEPARATELY':
            case 'HEAD_OF_HOUSEHOLD':
            default:
                return { base: 25000, phaseout: 34000 };
        }
    }

    /**
     * Calculate Average Indexed Monthly Earnings (AIME)
     *
     * This is a simplified calculation that assumes:
     * - Consistent income throughout working career
     * - Current income represents indexed earnings
     * - Working from age 22 to retirement age or current age
     */
    private calculateAIME(): number {
        // Determine working years - from age 22 to retirement or current age
        const startWorkingAge = 22;
        const endWorkingAge = Math.max(
            this.currentAge,
            this.socialSecurityBenefitAge,
        );
        const actualWorkingYears = Math.max(0, endWorkingAge - startWorkingAge);

        // Cap income at Social Security taxable maximum
        const cappedAnnualIncome = Math.min(
            this.grossAnnualIncome,
            SocialSecurityBenefitCalculator.MAXIMUM_TAXABLE_EARNINGS_2025,
        );

        // Calculate total indexed earnings
        // In a real implementation, this would involve indexing historical earnings
        // Here we assume current income level for all working years
        const totalIndexedEarnings =
            cappedAnnualIncome *
            Math.min(
                actualWorkingYears,
                SocialSecurityBenefitCalculator.WORKING_YEARS,
            );

        // Calculate AIME (divide by 35 years * 12 months = 420 months)
        const totalMonths =
            SocialSecurityBenefitCalculator.WORKING_YEARS *
            SocialSecurityBenefitCalculator.MONTHS_PER_YEAR;
        const aime = totalIndexedEarnings / totalMonths;

        return aime;
    }

    /**
     * Calculate Primary Insurance Amount (PIA) using SSA bend points
     *
     * The PIA formula applies different percentages to different portions of AIME:
     * - 90% of first $1,226 of AIME
     * - 32% of AIME between $1,226 and $7,391
     * - 15% of AIME over $7,391
     */
    private calculatePIA(aime: number): number {
        let pia = 0;

        // First bend point: 90% of first $1,226
        if (aime > 0) {
            const firstSegment = Math.min(
                aime,
                SocialSecurityBenefitCalculator.BEND_POINT_1,
            );
            pia +=
                firstSegment * SocialSecurityBenefitCalculator.PIA_PERCENTAGE_1;
        }

        // Second bend point: 32% of amount between $1,226 and $7,391
        if (aime > SocialSecurityBenefitCalculator.BEND_POINT_1) {
            const secondSegment = Math.min(
                aime - SocialSecurityBenefitCalculator.BEND_POINT_1,
                SocialSecurityBenefitCalculator.BEND_POINT_2 -
                    SocialSecurityBenefitCalculator.BEND_POINT_1,
            );
            pia +=
                secondSegment *
                SocialSecurityBenefitCalculator.PIA_PERCENTAGE_2;
        }

        // Third segment: 15% of amount over $7,391
        if (aime > SocialSecurityBenefitCalculator.BEND_POINT_2) {
            const thirdSegment =
                aime - SocialSecurityBenefitCalculator.BEND_POINT_2;
            pia +=
                thirdSegment * SocialSecurityBenefitCalculator.PIA_PERCENTAGE_3;
        }

        return roundToTwoDecimals(pia);
    }

    /**
     * Apply age-based adjustments to PIA
     *
     * - Early retirement (before FRA): Reduced benefits
     * - Full retirement age: 100% of PIA
     * - Delayed retirement (after FRA, up to 70): Increased benefits
     */
    private applyAgeAdjustments(pia: number): number {
        const claimingAge = this.socialSecurityBenefitAge;
        const fullRetirementAge =
            SocialSecurityBenefitCalculator.FULL_RETIREMENT_AGE;

        if (claimingAge < fullRetirementAge) {
            const monthsEarly = (fullRetirementAge - claimingAge) * 12;

            let reductionPercentage = 0;
            if (monthsEarly <= 36) {
                // 5/9% per month for first 36 months
                reductionPercentage = (monthsEarly * (5 / 9)) / 100;
            } else {
                // 5/9% for first 36 months + 5/12% for additional months
                reductionPercentage =
                    (36 * (5 / 9)) / 100 +
                    ((monthsEarly - 36) * (5 / 12)) / 100;
            }

            return pia * (1 - reductionPercentage);
        } else if (claimingAge > fullRetirementAge) {
            // Delayed retirement credit (up to age 70)
            const yearsDelayed = Math.min(claimingAge - fullRetirementAge, 3); // Max 3 years of credits (age 70)
            const increasePercentage =
                yearsDelayed *
                SocialSecurityBenefitCalculator.DELAYED_RETIREMENT_CREDIT_PER_YEAR;
            return pia * (1 + increasePercentage);
        } else {
            // Full retirement age - no adjustment
            return pia;
        }
    }
}
